import { makeAutoObservable } from "mobx";
import { Volunteer, Organization, IOrganization } from ".";
import { IVolunteer } from "./Volunteer";
import { UserProfileTypes } from "../../data/UserProfileTypes";

export enum UserType {
    Volunteer,
    Organization,
    None
}

type UserProfile = { id: number, type: string, default: boolean, active: boolean, identity: Volunteer | Organization };
type ProfileData = UserProfile[];

export interface IUser {
    id: number;
    email: string;
    active: boolean;
    requiresSubscription: boolean;
    readonly registrationTimestamp: number;
    profileData?: ProfileData;
}

export class User implements IUser {
    id = -1;
    email = "";
    active = false;
    requiresSubscription = true;
    profileData?: ProfileData = undefined;

    readonly registrationTimestamp: number = 0;

    constructor(idOrUser?: number | IUser) {
        makeAutoObservable(this);

        if (idOrUser === undefined || typeof idOrUser === 'number') {
            this.id = idOrUser || -1;
        } else {
            this.id = idOrUser.id;
            this.email = idOrUser.email;
            if (idOrUser.profileData) {
                this.profileData = idOrUser.profileData.map(profile => {
                    if (profile.type === UserProfileTypes.Volunteer) {
                        return {
                            id: profile.id,
                            type: profile.type,
                            default: profile.default,
                            active: profile.active,
                            identity: new Volunteer(profile.identity as IVolunteer)
                        }
                    } else {
                        return {
                            id: profile.id,
                            type: profile.type,
                            default: profile.default,
                            active: profile.active,
                            identity: new Organization(profile.identity as IOrganization)
                        }
                    }
                });
            }
            this.active = idOrUser.active;
            this.requiresSubscription = idOrUser.requiresSubscription;
            this.registrationTimestamp = idOrUser.registrationTimestamp;
        }
    }

    get registrationDate(): Date {
        return new Date(this.registrationTimestamp);
    }

    get volunteer(): Volunteer | undefined {
        return this.activeProfile && (this.activeProfile.type === UserProfileTypes.Volunteer) ? this.activeProfile.identity as Volunteer : undefined;
    }

    get organization(): Organization | undefined {
        return this.activeProfile && (this.activeProfile.type === UserProfileTypes.Nonprofit || this.activeProfile.type === UserProfileTypes.ForProfit) ? this.activeProfile.identity as Organization : undefined;
    }

    get hasVolunteerProfile(): boolean {
        return this.profileData ? this.profileData.findIndex(profile => profile.type === UserProfileTypes.Volunteer) !== -1 : false;
    }

    // Get the volunteer profile even if it isn't the active profile
    get volunteerProfile() {
        return this.profileData?.find(profile => profile.type === UserProfileTypes.Volunteer) as (Omit<UserProfile, 'identity'> & { identity: Volunteer }) | undefined;
    }

    get isVolunteerProfileActive(): boolean {
        // TODO: Switch to using more .volunteer calls than isVolunteerProfileActive calls
        return this.activeProfile?.type === UserProfileTypes.Volunteer;
    }

    get hasOrganizationProfile(): boolean {
        return this.profileData ? this.profileData.findIndex(profile => profile.type === UserProfileTypes.Nonprofit || profile.type === UserProfileTypes.ForProfit) !== -1 : false;
    }

    // TODO: Remove once the 1 organization profile per user limitation is lifted
    get organizationProfile() {
        return this.profileData?.find(profile => profile.type === UserProfileTypes.Nonprofit || profile.type === UserProfileTypes.ForProfit) as (Omit<UserProfile, 'identity'> & { identity: Organization }) | undefined;
    }

    get isOrganizationProfileActive(): boolean {
        return this.activeProfile?.type === UserProfileTypes.Nonprofit || this.activeProfile?.type === UserProfileTypes.ForProfit;
    }

    get activeProfile() {
        return this.profileData?.find(profile => profile.active);
    }

    get name() {
        if (this.activeProfile === undefined) {
            return undefined;
        } else if (this.activeProfile.identity instanceof Volunteer) {
            return this.activeProfile.identity.firstName;
        } else {
            return (this.activeProfile.identity as Organization).name;
        }
    }

    get numProfiles() {
        return this.profileData?.length || 0;
    }

    // Setters

    setActive(active: boolean) {
        this.active = active;
    }

    // TODO: Should be able to remove after the volunteer API requests are updated to 
    // apply changes to the user's profileData property rather than the volunteer 
    // store's volunteer property.
    updateVolunteerProfile(updatedProfile: Volunteer) {
        const updatedProfileData = (this.profileData || [])?.map(profile => {
            if (profile.type === UserProfileTypes.Volunteer) {
                return {
                    id: profile.id,
                    type: profile.type,
                    default: profile.default,
                    active: profile.active,
                    identity: updatedProfile
                }
            } else {
                return profile;
            }
        });
        this.profileData = updatedProfileData;
    }
}